import React from "react";

import { isNotNullOrEmpty, convertJsonTextToHtml } from "../lib/utilities";
import { useEntityById } from "../lib/hooks";
import BookList from "./bookList";
import TrailerSlider from "./trailerSlider";
import TrailerViewer from "./trailerViewer";
import NavBackButton from "./navBackButton";
import * as api from "../lib/api";
import { BoxSetNumber } from "../lib/constants";

const enrichBook = (book) => {
  if (isNotNullOrEmpty(book.series)) {
    // get all books in series, add to book obj
    const series = api.getBookItemsBySeriesId(book.series[0]);
    const seriesItems = api.getSeriesById(book.series[0]);
    const seriesItem = isNotNullOrEmpty(seriesItems)
      ? seriesItems[0]
      : { title: "Series" };
    book = {
      ...book,
      seriesBooks: api.sortBySeriesOrder(series),
      seriesTitle: seriesItem.title,
      seriesRoute: seriesItem.route,
    };
  }

  // get related books, add to book obj
  if (isNotNullOrEmpty(book.related)) {
    let relatedItems = [];
    book.related.forEach((relId) => {
      const relBook = api.getBooksById(relId);
      if (isNotNullOrEmpty(relBook)) {
        relatedItems.push(relBook[0]);
      }
    });
    book = { ...book, relatedItems: [...relatedItems] };
  }

  return book;
};

const BookView = ({ id, extraContent = true }) => {
  const item = useEntityById(id, api.getBooksById, enrichBook);
  const today = new Date();

  if (!item) return null;

  const getSeriesBookSubTitleLink = (item) => {
    if (isNotNullOrEmpty(item.seriesTitle) && item.seriesNum !== BoxSetNumber)
      return <a href={`/series/${item.seriesRoute}`}>{item.seriesTitle}</a>;

    return undefined;
  };

  const seriesBookSubTitle =
    isNotNullOrEmpty(item.seriesTitle) && item.seriesNum !== BoxSetNumber
      ? `Book ${item.seriesNum} of`
      : "";

  const getAudioBookUrl = (audioId) => {
    if (!!audioId && audioId.indexOf("http") < 0) {
      return `https://www.amazon.com/dp/${audioId}`;
    }

    return audioId;
  };

  const getOverlay = (item) => {
    if (new Date(item.pubDate) > today) {
      const imgtype = isNotNullOrEmpty(item.amazonId)
        ? "preorder"
        : "comingsoon";
      return (
        <img
          alt={`${imgtype} ${item.title}`}
          className="book-ribbon-large"
          src={`/assets/misc/${imgtype}.png`}
        />
      );
    }

    return undefined;
  };

  const getPublishedInfo = (item) => {
    if (new Date(item.pubDate) > today) {
      return (
        <p>
          <em>Coming: {item.pubDate}!</em>
        </p>
      );
    }

    return <p>Published: {item.pubDate}</p>;
  };

  const getTrailerContent = (item) => {
    const trailers = []; //TODO: handle when a book has more than 1 trailer

    if (isNotNullOrEmpty(trailers) || isNotNullOrEmpty(item.trailerUrl)) {
      const caption = `Watch the Trailer${trailers.length > 1 ? "s" : ""}`;
      return (
        <div>
          <h5 style={{ marginTop: 30 }}>{caption}</h5>
          {trailers.length > 1 ? (
            <TrailerSlider items={trailers} />
          ) : (
            <TrailerViewer itemId={item.id} videoId={item.trailerUrl} />
          )}
        </div>
      );
    }

    return undefined;
  };

  return (
    <div className={`section ${extraContent ? "contents" : ""}`}>
      <div className="content">
        <div className="row">
          <div className="col-12 col-md-8">
            <h2 className="book-view-title">{item.title}</h2>
            <h5>
              {item.subTitle ?? seriesBookSubTitle ?? ""}{" "}
              {getSeriesBookSubTitleLink(item)}
            </h5>
            <p
              dangerouslySetInnerHTML={{
                __html: convertJsonTextToHtml(item.description),
              }}
            />
          </div>
          <div className="col-12 col-md-4">
            {extraContent && <NavBackButton />}
            <div key={item.id}>
              <div className="book-frame-large">
                <img
                  alt={item.title}
                  className="book-large"
                  src={`/assets/books/${item.id}.jpg`}
                />
                {getOverlay(item)}
              </div>
              {getPublishedInfo(item)}
              <div className="book-view-buy">
                <a
                  title={item.title}
                  href={`https://www.amazon.com/dp/${item.amazonId}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <img
                    alt="buy from amazon"
                    className="amazon-button-image"
                    src="/assets/misc/amazon-black.jpg"
                  />
                </a>
              </div>
              {isNotNullOrEmpty(item.audioId) && (
                <div className="audio-view-buy">
                  <a
                    title={item.title}
                    href={getAudioBookUrl(item.audioId)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <img
                      alt="buy audiobook"
                      className="amazon-button-image"
                      src="/assets/misc/audio-book.jpg"
                    />
                  </a>
                </div>
              )}
            </div>
          </div>
          {extraContent && (
            <div className="row">
              <div className="col-12">
                {getTrailerContent(item)}
                {isNotNullOrEmpty(item.keywords) && (
                  <p className="d-none d-md-block">
                    Keywords: <em>{item.keywords.join(", ")}</em>
                  </p>
                )}
                {isNotNullOrEmpty(item.seriesBooks) && (
                  <>
                    {isNotNullOrEmpty(item.seriesTitle) && (
                      <h4 style={{ marginTop: 40 }}>
                        <a href={`/series/${item.seriesRoute}`}>
                          {item.seriesTitle}
                        </a>
                      </h4>
                    )}
                    <BookList items={item.seriesBooks} />
                  </>
                )}
                {isNotNullOrEmpty(item.relatedItems) && (
                  <>
                    <h4 style={{ marginTop: 40 }}>Related Items</h4>
                    <BookList items={api.sortByPubDate(item.relatedItems)} />
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookView;
